import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Button,
  Grid,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Collapse,
  LinearProgress
} from '@mui/material';
import {
  Groups,
  CheckCircle,
  Refresh,
  AccessTime,
  EventSeat,
  QrCode2,
  Download,
  Share,
  ChevronRight,
  ExpandMore,
  Language,
  WarningAmber,
  ArrowForward
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import { GRADIENTS, RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';
import LibrarianAnalyticsCards from './LibrarianAnalyticsCards';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

/**
 * Librarian Home - Mobile-First Dashboard Overview
 */
function LibrarianHome({ showSnackbar }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [library, setLibrary] = useState(null);
  const [completion, setCompletion] = useState(null);
  const [activeSessions, setActiveSessions] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [qrExpanded, setQrExpanded] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsError, setAnalyticsError] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch librarian's assigned library
      const libraryResponse = await api.get('/librarian/library');

      if (!libraryResponse.data.data.library) {
        showSnackbar('No library assigned to your account.', 'error');
        setLoading(false);
        return;
      }

      const myLibrary = libraryResponse.data.data.library;
      setLibrary(myLibrary);
      setCompletion(libraryResponse.data.data.completion ?? null);

      // Build library QR code URL (served by backend at /api/qr/library/:id)
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const baseUrl = apiUrl.replace('/api', '');
      setQrCodeUrl(`${baseUrl}/api/qr/library/${myLibrary.id}`);

      // Fetch active sessions
      try {
        const sessionsResponse = await api.get('/librarian/sessions', {
          params: { library_id: myLibrary.id, active: true }
        });
        setActiveSessions(sessionsResponse.data.data.sessions || []);
      } catch (err) {
        setActiveSessions([]);
      }

      // Fetch upcoming bookings from the precise server-side endpoint
      try {
        const upcomingResponse = await api.get('/librarian/bookings/upcoming', {
          params: { page: 1, limit: 5 }
        });
        setUpcomingBookings(upcomingResponse.data.data.bookings || []);
      } catch (err) {
        setUpcomingBookings([]);
      }

      // Fetch analytics overview — runs independently; failure does not block the rest
      try {
        const analyticsResponse = await api.get('/librarian/analytics/overview');
        setAnalytics(analyticsResponse.data.data);
        setAnalyticsError(false);
      } catch (err) {
        setAnalyticsError(true);
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      showSnackbar(err.response?.data?.message || 'Failed to load dashboard', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadQR = () => {
    if (!qrCodeUrl) return;
    const link = document.createElement('a');
    link.href = qrCodeUrl;
    link.download = `${library?.name || 'library'}-qr-code.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showSnackbar('QR code downloaded!', 'success');
  };

  const handleShareQR = async () => {
    if (!qrCodeUrl || !library) return;
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${library.name} - QR Code`,
          text: `Scan this QR code to check in/out at ${library.name}`,
          url: qrCodeUrl
        });
      } else {
        await navigator.clipboard.writeText(qrCodeUrl);
        showSnackbar('QR code URL copied to clipboard!', 'info');
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent' }}>
      <Container maxWidth="sm" sx={{ py: 2, pb: 10 }}>
        <MotionBox
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Header */}
          <MotionBox variants={itemVariants} mb={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h4" fontWeight="800" gutterBottom>
                  Dashboard
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Typography variant="body2" color="text.secondary">
                    Welcome back, {user?.name}!
                  </Typography>
                  {library?.capacity != null && (
                    <Chip
                      size="small"
                      icon={<EventSeat sx={{ fontSize: 16 }} />}
                      label={`${library.capacity} Seats`}
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 700, height: 24 }}
                    />
                  )}
                </Box>
              </Box>
              <IconButton
                onClick={fetchDashboardData}
                sx={{
                  bgcolor: 'action.hover',
                  '&:hover': { bgcolor: 'action.selected' }
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </MotionBox>

          {/* Profile Completion Reminder — shown until profile is at least 80% complete */}
          {completion && (completion.percentage ?? 0) < 80 && (
            <MotionCard
              variants={itemVariants}
              onClick={() => navigate('/librarian/library-details')}
              sx={{
                borderRadius: RADIUS.xl,
                mb: 2,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'warning.main',
                bgcolor: (t) =>
                  t.palette.mode === 'dark'
                    ? 'rgba(245, 158, 11, 0.12)'
                    : 'rgba(245, 158, 11, 0.08)'
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'warning.main', color: 'warning.contrastText' }}>
                    <WarningAmber fontSize="small" />
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="700">
                      Complete your library profile
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      A complete profile attracts more students.
                    </Typography>
                  </Box>
                  <ArrowForward sx={{ color: 'warning.main' }} />
                </Box>

                <Box display="flex" alignItems="center" gap={1} mb={0.75}>
                  <Box flex={1}>
                    <LinearProgress
                      variant="determinate"
                      value={completion.percentage ?? 0}
                      color="warning"
                      sx={{ height: 8, borderRadius: 5 }}
                    />
                  </Box>
                  <Typography variant="body2" fontWeight="700" color="warning.main">
                    {completion.percentage ?? 0}%
                  </Typography>
                </Box>

                {Array.isArray(completion.missing) && completion.missing.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={0.75} mt={1.5}>
                    {completion.missing.slice(0, 4).map((item) => (
                      <Chip
                        key={item}
                        label={item}
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ fontWeight: 500 }}
                      />
                    ))}
                    {completion.missing.length > 4 && (
                      <Chip
                        label={`+${completion.missing.length - 4} more`}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 600 }}
                      />
                    )}
                  </Box>
                )}
              </CardContent>
            </MotionCard>
          )}

          {/* Library Check-in QR (collapsible, closed by default) */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box
                role="button"
                tabIndex={0}
                aria-expanded={qrExpanded}
                aria-controls="librarian-qr-section"
                onClick={() => setQrExpanded((v) => !v)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setQrExpanded((v) => !v);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  borderRadius: RADIUS.md,
                  outline: 'none',
                  '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` }
                }}
              >
                <Avatar sx={{ width: 40, height: 40, background: GRADIENTS.brand }}>
                  <QrCode2 fontSize="small" />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="700">
                    Library Check-in QR
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {qrExpanded ? 'Tap to hide' : 'Tap to show QR'}
                  </Typography>
                </Box>
                <ExpandMore
                  sx={{
                    transform: qrExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.25s ease',
                    color: 'text.secondary'
                  }}
                />
              </Box>

              <Collapse in={qrExpanded} timeout="auto" unmountOnExit>
                <Box id="librarian-qr-section" mt={2}>
                  <Box
                    sx={{
                      bgcolor: 'white',
                      p: 2,
                      borderRadius: RADIUS.lg,
                      textAlign: 'center',
                      border: '2px dashed',
                      borderColor: 'divider',
                      mb: 2
                    }}
                  >
                    {qrCodeUrl ? (
                      <img
                        src={qrCodeUrl}
                        alt="Library QR Code"
                        style={{
                          width: '100%',
                          maxWidth: 240,
                          height: 'auto',
                          margin: '0 auto',
                          display: 'block'
                        }}
                      />
                    ) : (
                      <Box py={5}>
                        <QrCode2 sx={{ fontSize: 64, color: 'text.secondary' }} />
                        <Typography variant="body2" color="text.secondary" mt={1}>
                          QR code unavailable
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="outlined"
                        size="small"
                        startIcon={<Download />}
                        onClick={handleDownloadQR}
                        disabled={!qrCodeUrl}
                        sx={{ borderRadius: RADIUS.md, py: 1, textTransform: 'none', fontWeight: 600 }}
                      >
                        Download
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="small"
                        startIcon={<Share />}
                        onClick={handleShareQR}
                        disabled={!qrCodeUrl}
                        sx={{
                          borderRadius: RADIUS.md,
                          py: 1,
                          textTransform: 'none',
                          fontWeight: 600,
                          background: GRADIENTS.brand
                        }}
                      >
                        Share
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Collapse>
            </CardContent>
          </MotionCard>

          {/* Website — highlighted hero CTA */}
          <MotionCard
            variants={itemVariants}
            animate={{
              boxShadow: [
                '0 2px 12px rgba(255, 107, 0, 0.18)',
                '0 6px 28px rgba(255, 107, 0, 0.45)',
                '0 2px 12px rgba(255, 107, 0, 0.18)'
              ]
            }}
            transition={{ repeat: Infinity, repeatType: 'reverse', duration: 2, ease: 'easeInOut' }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            sx={{
              borderRadius: RADIUS.xl,
              mb: 2,
              background: GRADIENTS.brand,
              overflow: 'hidden',
              position: 'relative',
              cursor: 'pointer'
            }}
          >
            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => navigate('/librarian/website')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate('/librarian/website');
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  outline: 'none',
                  '&:focus-visible': { boxShadow: '0 0 0 2px rgba(255,255,255,0.8)' }
                }}
              >
                <Avatar
                  sx={{
                    width: 44,
                    height: 44,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)'
                  }}
                >
                  <Language sx={{ color: 'white' }} />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="subtitle1" fontWeight="700" sx={{ color: 'white' }}>
                    Your Website
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'white', opacity: 0.9 }}>
                    Customise &amp; share your public page
                  </Typography>
                </Box>
                <MotionBox
                  animate={{ x: [0, 4, 0] }}
                  transition={{ repeat: Infinity, repeatType: 'loop', duration: 1.5, ease: 'easeInOut' }}
                >
                  <ChevronRight sx={{ color: 'white' }} />
                </MotionBox>
              </Box>
            </CardContent>
          </MotionCard>

          {/* Analytics Overview Cards */}
          <MotionBox variants={itemVariants}>
            <LibrarianAnalyticsCards
              data={analytics}
              loading={loading}
              error={analyticsError}
              onRetry={fetchDashboardData}
              upcomingCount={upcomingBookings.length}
            />
          </MotionBox>

          {/* Active Sessions Preview */}
          <MotionCard variants={itemVariants} sx={{ borderRadius: RADIUS.xl, mb: 2 }}>
            <CardContent sx={{ p: 0 }}>
              <Box p={2} pb={1} display="flex" justifyContent="space-between" alignItems="center">
                <Box display="flex" alignItems="center" gap={1}>
                  <Groups color="primary" />
                  <Typography variant="subtitle1" fontWeight="700">
                    Active Sessions
                  </Typography>
                </Box>
                <Chip
                  label={`${activeSessions.length} Active`}
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
              <Divider />
              {activeSessions.length === 0 ? (
                <Box p={3} textAlign="center">
                  <Typography variant="body2" color="text.secondary">
                    No active sessions at the moment
                  </Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {activeSessions.slice(0, 3).map((session, index) => (
                    <React.Fragment key={session.id}>
                      {index > 0 && <Divider />}
                      <ListItem sx={{ py: 1.5 }}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              background: GRADIENTS.brand
                            }}
                          >
                            {session.student?.name?.charAt(0).toUpperCase()}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={session.student?.name || 'Unknown'}
                          secondary={
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <AccessTime fontSize="small" />
                              {new Date(session.entered_at).toLocaleTimeString()}
                            </Box>
                          }
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        <Chip
                          label="In Session"
                          color="success"
                          size="small"
                          icon={<CheckCircle />}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                  {activeSessions.length > 3 && (
                    <>
                      <Divider />
                      <ListItem
                        button
                        onClick={() => showSnackbar('Navigate to Sessions tab', 'info')}
                        sx={{ justifyContent: 'center', py: 1 }}
                      >
                        <Typography variant="body2" color="primary" fontWeight="600">
                          View All {activeSessions.length} Sessions
                        </Typography>
                        <ChevronRight color="primary" fontSize="small" />
                      </ListItem>
                    </>
                  )}
                </List>
              )}
            </CardContent>
          </MotionCard>
        </MotionBox>
      </Container>
    </Box>
  );
}

export default LibrarianHome;
